/**
 * The roadmap, as a page of the docs: how MOR utility joins Morcast after V1. It comes in steps
 * that the Morcast team opens as the network of brands and creators grows, so the page shows
 * states ("Now", "With growth") and never dates. The steps are the open network of today, a
 * lower fee for brands that stake MOR toward the Morcast subnet in Morpheus, premium levels that
 * set limits by stake, and Morpheus tournaments without a fee.
 *
 * Nothing on this page is built yet. The statements follow the roadmap document
 * (docs/roadmap.md, decision D29). The amounts of a step are set when it opens, so every figure
 * here is an example and says so.
 */

import {
  CalendarClock,
  DoorOpen,
  Gift,
  Layers,
  type LucideIcon,
  Megaphone,
  ScanSearch,
  SlidersHorizontal,
  Sparkles,
  Trophy,
} from "lucide-react";
import type { ReactNode } from "react";

import { glass, inset } from "../../components/marketing.js";
import { Badge, IconTile } from "../../components/ui.js";
import { exampleLabel } from "../home/visuals.js";
import { DocPage, DocPanel, DocSection, P, Points } from "./parts.js";

/**
 * The steps in the order planned today. Only the first is open. The others open when the network
 * is ready for them, so each carries a state in place of a date.
 */
const steps: { title: string; text: string; open: boolean }[] = [
  {
    title: "Open network",
    text: "Nobody needs MOR. Every brand and creator has the full allowance, and Morcast's fee is 20%.",
    open: true,
  },
  {
    title: "A lower fee for staking brands",
    text: "A brand's fee falls with its share of the MOR staked toward Morcast.",
    open: false,
  },
  {
    title: "Premium levels",
    text: "A wallet's stake sets its level. Larger campaigns and the full creator allowance need one.",
    open: false,
  },
];

/** How a wallet's MOR stake is found and counted, which every step after the first relies on. */
const stakeFacts: { icon: LucideIcon; title: string; text: string }[] = [
  {
    icon: Layers,
    title: "Staked in Morpheus",
    text: "Stake MOR toward the Morcast subnet in the Morpheus Builders contract on Base. It stays your MOR, and nothing is paid to Morcast.",
  },
  {
    icon: ScanSearch,
    title: "Read from the chain",
    text: "Morcast reads the stake of the wallet you sign in with, straight from that contract.",
  },
  {
    icon: CalendarClock,
    title: "Counted after a month",
    text: "MOR counts once it has been staked for at least a month. A withdrawal lowers your stake at once.",
  },
  {
    icon: SlidersHorizontal,
    title: "Set when a step opens",
    text: "How much MOR a level needs is set when its step opens, for the network as it is then.",
  },
];

/**
 * The fee example: a 50,000 USDC campaign that reaches its target. Creators always receive 80% of
 * it. The brand's share of the stake comes off Morcast's 20%, up to all of it. `fee` and `saved`
 * are in points of the campaign total, and each bar is drawn from them.
 */
const feeCases: { share: string; fee: number; saved: number; feeAmount: string; pays: string }[] = [
  { share: "No stake", fee: 20, saved: 0, feeAmount: "10,000 USDC", pays: "50,000 USDC" },
  { share: "10% of the stake", fee: 10, saved: 10, feeAmount: "5,000 USDC", pays: "45,000 USDC" },
  { share: "20% of the stake or more", fee: 0, saved: 20, feeAmount: "0", pays: "40,000 USDC" },
];

/** The colours of the fee example's bars, shared by the bars and their legend. */
const feeTones = {
  creators: "bg-gradient-to-r from-brand-600 to-brand-400",
  fee: "bg-gradient-to-r from-brand-300 to-brand-200",
  saved: "bg-zinc-300 dark:bg-white/15",
} as const;

/** A level's row in a table of example limits: its name, the stake it stands for, its limits. */
interface LevelRow {
  level: string;
  stake: string;
  values: string[];
}

const brandLevels: LevelRow[] = [
  { level: "Base", stake: "No stake", values: ["1,000,000"] },
  { level: "Middle", stake: "A stake", values: ["10,000,000"] },
  { level: "Top", stake: "A larger stake", values: ["Higher still"] },
];

const creatorLevels: LevelRow[] = [
  { level: "Base", stake: "No stake", values: ["3", "1"] },
  { level: "Middle", stake: "A stake", values: ["3", "2"] },
  { level: "Top", stake: "A larger stake", values: ["5", "3"] },
];

export function Roadmap() {
  const note = "mt-3 text-xs leading-5 text-zinc-500 dark:text-zinc-400";
  return (
    <DocPage
      group="Roadmap"
      title="MOR utility grows with the network"
      documentTitle="Roadmap"
      lead="Morcast is open to every brand and creator today, and nobody needs MOR to use it. MOR utility comes one step at a time. There are no dates. The Morcast team opens each step when enough brands and creators are on the network."
    >
      <StepsCard />

      <DocSection id="stake" title="Stake toward Morcast and keep your MOR">
        <P>
          Every later step reads one thing, the MOR a wallet has staked toward the Morcast subnet in
          Morpheus.
        </P>
        <ul className="grid gap-3 sm:grid-cols-2">
          {stakeFacts.map(({ icon, title, text }) => (
            <li key={title} className={`flex gap-4 p-5 ${glass}`}>
              <IconTile icon={icon} />
              <div className="min-w-0">
                <h3 className="font-display font-semibold tracking-[-0.01em] text-zinc-950 dark:text-white">
                  {title}
                </h3>
                <p className="mt-1.5 text-sm leading-6 text-zinc-600 dark:text-zinc-400">{text}</p>
              </div>
            </li>
          ))}
        </ul>
      </DocSection>

      <DocSection id="lower-fee" title="A lower fee for brands that stake">
        <P>A brand's fee falls by its share of all the MOR staked toward Morcast.</P>
        <Points
          items={[
            { text: "A brand that holds 10% of the stake pays a fee of 10% in place of 20%." },
            {
              text: "The reduction stops at 20 points. A brand with 20% of the stake or more pays no fee, so at most five brands can pay none at one time.",
            },
            {
              text: "The reduction comes out of Morcast's share. Creators receive the same amount whatever the brand has staked.",
            },
          ]}
        />
        <FeeExample />
      </DocSection>

      <DocSection id="levels" title="Premium levels, set by stake">
        <P>
          Every wallet gets a level from the MOR it has staked. A level opens larger campaigns to a
          brand and more room to a creator. A wallet with no MOR stays at the base level and still
          takes part.
        </P>
        <DocPanel
          id="levels-brands"
          icon={Megaphone}
          title="For brands"
          description="A level sets the largest target a campaign may ask for."
        >
          <LevelTable
            caption="Example of the largest target per campaign at each level"
            columns={["Largest target per campaign"]}
            rows={brandLevels}
          />
          <p className={note}>A target counts impressions on X and views on YouTube.</p>
          <LevelNote icon={Gift} title="One free trial">
            Every new brand gets one campaign at the middle level without staking. Its budget and
            its fee are those of any campaign.
          </LevelNote>
        </DocPanel>
        <DocPanel
          id="levels-creators"
          icon={Sparkles}
          title="For creators"
          description="A level sets your accounts per campaign and the items each may submit."
        >
          <LevelTable
            caption="Example of the accounts and items per campaign at each level"
            columns={["Accounts per campaign", "Items per account"]}
            rows={creatorLevels}
          />
          <p className={note}>
            The top level is what every creator has today. A campaign's own limits still apply.
          </p>
          <LevelNote icon={DoorOpen} title="Open at every level">
            A creator with no MOR still takes part in any campaign, at the base level, and is paid
            by the same rules.
          </LevelNote>
        </DocPanel>
      </DocSection>

      <DocSection id="tournaments" title="Morpheus tournaments, with no fee">
        <P>
          When the Morpheus protocol ships an update, Morcast opens a tournament, which is a
          campaign that spreads the word.
        </P>
        <Points
          items={[
            { text: "It is shown first among the campaigns for as long as it is open." },
            { text: "Morcast takes no fee. All that the campaign pays goes to creators." },
            {
              text: "It runs like any campaign. The budget waits in the escrow, every item is checked against the brief, and the result is published with its evidence.",
            },
            { text: "Who funds a tournament is decided for each one when it opens." },
          ]}
        />
      </DocSection>
    </DocPage>
  );
}

/**
 * The page's first panel: the steps in order, joined by a line, each with its state. The open step
 * has a filled marker and a green "Now". The tournaments stand apart below, because they follow
 * Morpheus's updates and can run beside any step.
 */
function StepsCard() {
  const title = "font-display font-semibold tracking-[-0.01em] text-zinc-950 dark:text-white";
  const text = "mt-1 text-sm leading-6 text-zinc-600 dark:text-zinc-400";
  const marker = "grid size-9 shrink-0 place-items-center rounded-full font-display text-sm";
  const waiting =
    "bg-white font-medium text-zinc-500 ring-1 ring-zinc-900/10 ring-inset dark:bg-white/[0.04] dark:text-zinc-400 dark:ring-white/10";
  return (
    <section aria-labelledby="steps" className={`p-5 sm:p-7 ${glass}`}>
      <h2 id="steps" className={exampleLabel}>
        The steps
      </h2>
      <ol className="mt-6">
        {steps.map((step, index) => (
          <li
            key={step.title}
            className="relative flex gap-4 pb-7 before:absolute before:top-11 before:bottom-2 before:left-[1.0625rem] before:w-px before:bg-gradient-to-b before:from-brand-500/40 before:to-zinc-900/[0.06] last:pb-0 last:before:hidden dark:before:from-brand-300/25 dark:before:to-white/[0.05]"
          >
            <span
              className={`${marker} ${
                step.open
                  ? "bg-gradient-to-br from-brand-300 to-brand-500 font-semibold text-ink-950 shadow-[inset_0_1px_0_rgb(255_255_255/0.5),0_8px_24px_-8px_rgb(103_194_222/0.7)] dark:from-brand-400/[0.16] dark:to-brand-400/[0.05] dark:text-brand-200 dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.08)] dark:ring-1 dark:ring-brand-300/20 dark:ring-inset"
                  : waiting
              }`}
            >
              {index + 1}
            </span>
            <div className="min-w-0">
              <Badge tone={step.open ? "green" : "grey"}>{step.open ? "Now" : "With growth"}</Badge>
              <h3 className={`mt-2 ${title}`}>{step.title}</h3>
              <p className={text}>{step.text}</p>
            </div>
          </li>
        ))}
      </ol>
      <div className="mt-7 flex gap-4 border-t border-zinc-900/[0.07] pt-6 dark:border-white/[0.08]">
        <span className={`${marker} ${waiting}`}>
          <Trophy aria-hidden className="size-4" />
        </span>
        <div className="min-w-0">
          <Badge tone="grey">With Morpheus updates</Badge>
          <h3 className={`mt-2 ${title}`}>Morpheus tournaments</h3>
          <p className={text}>Campaigns that spread a Morpheus update, with no fee to Morcast.</p>
        </div>
      </div>
    </section>
  );
}

/**
 * The fee example: for three shares of the stake, what the brand pays and how it divides. Each
 * bar is as wide as the whole campaign and shows what creators receive, Morcast's fee and the
 * part the brand no longer pays. The bars only draw the amounts beside them, so assistive
 * technology skips them.
 */
function FeeExample() {
  const legend: [keyof typeof feeTones, string][] = [
    ["creators", "Creators"],
    ["fee", "Morcast's fee"],
    ["saved", "Not paid by the brand"],
  ];
  return (
    <figure className={`p-5 text-sm sm:p-7 ${glass}`}>
      <figcaption className={exampleLabel}>Example</figcaption>
      <p className="mt-2 font-display font-semibold text-zinc-950 dark:text-white">
        A 50,000 USDC campaign that reaches its target
      </p>
      <ul className="mt-6 space-y-6">
        {feeCases.map((feeCase) => (
          <li key={feeCase.share}>
            <div className="flex flex-col gap-y-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-x-4">
              <p className="font-medium text-zinc-900 dark:text-zinc-100">{feeCase.share}</p>
              <p className="text-zinc-600 dark:text-zinc-400">
                The brand pays{" "}
                <span className="font-display font-semibold text-zinc-950 tabular-nums dark:text-white">
                  {feeCase.pays}
                </span>
              </p>
            </div>
            <div aria-hidden className="mt-2.5 flex h-3 gap-1 overflow-hidden rounded-full">
              <span className={`rounded-full ${feeTones.creators}`} style={{ width: "80%" }} />
              {feeCase.fee > 0 && (
                <span
                  className={`rounded-full ${feeTones.fee}`}
                  style={{ width: `${feeCase.fee}%` }}
                />
              )}
              {feeCase.saved > 0 && (
                <span
                  className={`rounded-full ${feeTones.saved}`}
                  style={{ width: `${feeCase.saved}%` }}
                />
              )}
            </div>
            <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
              Creators receive 40,000 USDC. Morcast's fee is {feeCase.feeAmount}.
            </p>
          </li>
        ))}
      </ul>
      <ul
        aria-hidden
        className="mt-6 flex flex-wrap gap-x-5 gap-y-2 border-t border-zinc-900/[0.07] pt-4 text-xs text-zinc-600 dark:border-white/[0.08] dark:text-zinc-400"
      >
        {legend.map(([tone, label]) => (
          <li key={tone} className="flex items-center gap-2">
            <span className={`size-2.5 rounded-full ${feeTones[tone]}`} />
            {label}
          </li>
        ))}
      </ul>
    </figure>
  );
}

/** A point set apart under a table of levels: an icon, a title and a sentence or two. */
function LevelNote({
  icon: Icon,
  title,
  children,
}: {
  icon: LucideIcon;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className={`mt-6 flex gap-3.5 p-4 ${inset}`}>
      <Icon aria-hidden className="mt-0.5 size-5 shrink-0 text-brand-600 dark:text-brand-400" />
      <div>
        <h4 className="font-display font-semibold text-zinc-950 dark:text-white">{title}</h4>
        <p className="mt-1 text-sm leading-6 text-zinc-600 dark:text-zinc-400">{children}</p>
      </div>
    </div>
  );
}

/**
 * A table of example limits, one row per level. It is labelled "Example" above, and its caption
 * names it to screen readers. The first column names the level and the stake it stands for. The
 * others hold the limits, set as figures.
 */
function LevelTable({
  caption,
  columns,
  rows,
}: {
  caption: string;
  columns: string[];
  rows: LevelRow[];
}) {
  const heading = "px-3 py-3 text-xs font-medium text-zinc-500 sm:px-4 dark:text-zinc-400";
  return (
    <>
      <p className={exampleLabel}>Example</p>
      <div className={`mt-3 overflow-hidden ${inset}`}>
        <table className="w-full text-left text-sm">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr className="border-b border-zinc-900/[0.07] dark:border-white/[0.08]">
              <th scope="col" className={heading}>
                Level
              </th>
              {columns.map((column) => (
                <th key={column} scope="col" className={`${heading} text-right`}>
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900/[0.06] dark:divide-white/[0.06]">
            {rows.map((row) => (
              <tr key={row.level}>
                <th scope="row" className="px-3 py-3.5 font-normal sm:px-4">
                  <span className="block font-display font-semibold text-zinc-950 dark:text-white">
                    {row.level}
                  </span>
                  <span className="mt-0.5 block text-xs text-zinc-500 dark:text-zinc-400">
                    {row.stake}
                  </span>
                </th>
                {row.values.map((value, index) => (
                  <td
                    key={columns[index]}
                    className="px-3 py-3.5 text-right font-display font-semibold text-zinc-950 tabular-nums sm:px-4 dark:text-white"
                  >
                    {value}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
